import { type ReactNode } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../../components/ui/states';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../features/auth/auth-context';
import { performanceApi, performanceKeys } from '../../features/performance/api';
import { OkrMemberPicker } from '../../features/performance/components/okr-member-picker';
import { OkrPage } from '../../features/performance/components/okr-page';
import { OkrSearch } from '../../features/performance/okr-search';

export const Route = createFileRoute('/_app/okr/')({
  validateSearch: OkrSearch,
  component: MyOkr,
});

/**
 * OKR — hai tab lớn: của tôi, và của nhân sự mình quản lý (người dùng yêu cầu
 * 2026-09-22). APM-OKR2-a/e, mục trên thanh điều hướng.
 *
 * ## Vì sao một route riêng thay vì `/okr/<id của tôi>`
 *
 * Mục điều hướng phải là một đường dẫn **cố định**: `visibleNav` dựng danh
 * sách trước khi biết người dùng là ai, và một `to` phụ thuộc id sẽ buộc thanh
 * điều hướng chờ `/auth/me` xong mới vẽ được. `/okr` cũng là đường dẫn người
 * ta gõ tay và đánh dấu trang — và nó đúng cho mọi người, không chỉ cho người
 * đã copy link của chính mình.
 *
 * Không gắn quyền, cùng lý lẽ với `/performance`: ai cũng xem được mục tiêu
 * của **chính mình** (spec mục 10 đòi minh bạch với người bị đo). Tab thứ hai
 * chỉ hiện khi danh sách "đặt được cho ai" trả về ít nhất một người — hỏi
 * server chứ không suy từ danh sách quyền, vì `performance:set_goal` được cấp
 * cho **mọi** người dưới dạng có điều kiện (xem `PersonalGoalController`).
 *
 * ⚠️ `/okr/$userId` vẫn còn: nó là đích của deep-link từ bảng hiệu suất nhóm.
 * Hai đường vào cùng một trang, nhưng đường này là đường người ta **đi tìm**,
 * còn đường kia là đường người ta **được dẫn tới**.
 */
function MyOkr(): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  const subjects = useQuery({
    queryKey: performanceKeys.goalSubjects(),
    queryFn: performanceApi.goalSubjects,
    retry: false,
  });

  /*
    Trạng thái đánh giá kỳ trước của **cả danh sách** — APM-OKR5, người dùng yêu
    cầu 2026-09-22.

    Một lời gọi cho cả ô chọn, không phải một lượt mỗi người: ô này có tới 200
    dòng, và 200 lượt gọi về không theo thứ tự sẽ làm nhãn nhấp nháy khi từng
    cái tới.

    Loại kỳ đi theo **tab đang mở**: đang xem tab Tháng thì "kỳ trước" là tháng
    trước, không phải quý trước. Tab Chung không nói về loại kỳ nào nên rơi về
    quý — nhịp đánh giá thường dùng.
  */
  const statusKind = search.tab && search.tab !== 'overview' ? search.tab : 'quarter';
  const status = useQuery({
    queryKey: performanceKeys.okrReviewStatus(statusKind),
    queryFn: () => performanceApi.okrReviewStatus(statusKind),
    retry: false,
    enabled: (subjects.data ?? []).length > 0,
  });

  /*
    Chờ phiên khôi phục xong rồi mới vẽ — `OkrPage` chọn endpoint theo
    `user.id`, và gọi nhầm đường của người khác cho chính mình là 404 trên
    trang của chính mình.
  */
  if (!user) return <Skeleton className="h-64 w-full" />;

  /*
    Người mình quản lý, **trừ chính mình**: người có quyền cấp tổ chức nhận cả
    danh bạ từ endpoint ấy, và tên mình nằm trong đó. Để nguyên thì tab nhóm
    mời họ "đặt OKR cho chính mình" ở đúng chỗ tab kia đã làm.
  */
  const members = (subjects.data ?? []).filter((subject) => subject.userId !== user.id);
  const canSeeTeam = members.length > 0;
  const scope = canSeeTeam ? (search.scope ?? 'me') : 'me';
  const selected = scope === 'team' ? (search.member ?? members[0]?.userId ?? null) : null;

  return (
    <div className="flex flex-col gap-4">
      <Tabs
        value={scope}
        onValueChange={(next) =>
          void navigate({
            search: {
              ...search,
              scope: next === 'me' ? undefined : 'team',
              // Đổi tab thì bỏ luôn kỳ đang xem: `offset` của tab con không
              // mang nghĩa gì khi người được xem đổi.
              offset: undefined,
            },
          })
        }
      >
        <TabsList variant="glass">
          <TabsTrigger value="me">{t('okr.scopeMine')}</TabsTrigger>
          {canSeeTeam && <TabsTrigger value="team">{t('okr.scopeTeam')}</TabsTrigger>}
        </TabsList>
      </Tabs>

      {scope === 'team' && (
        <OkrMemberPicker
          members={members}
          status={status.data ?? []}
          selected={selected}
          onSelect={(userId) =>
            void navigate({ search: { ...search, member: userId, offset: undefined } })
          }
        />
      )}

      {/*
        `key` buộc `OkrPage` dựng lại khi đổi người: state bên trong nó (đang
        sửa mục nào, đang mở form thêm) thuộc về **một** người, và mang nó sang
        người khác là mở sẵn một form sửa cho mục tiêu của người vừa rời màn
        hình.
      */}
      <OkrPage
        key={selected ?? user.id}
        userId={selected ?? user.id}
        tab={search.tab ?? 'overview'}
        offset={search.offset ?? 0}
        onTabChange={(tab) =>
          // Đổi tab thì **quay về kỳ hiện tại**: `offset` của tab tháng và tab
          // năm không cùng đơn vị, nên mang `2` từ tab này sang tab kia là nhảy
          // hai năm khi người dùng chỉ vừa đi hai tháng.
          void navigate({
            search: { ...search, tab: tab === 'overview' ? undefined : tab, offset: undefined },
          })
        }
        onOffsetChange={(offset) =>
          void navigate({ search: { ...search, offset: offset === 0 ? undefined : offset } })
        }
        onOpenPeriod={(tab, offset) =>
          void navigate({
            search: {
              ...search,
              tab: tab === 'overview' ? undefined : tab,
              offset: offset === 0 ? undefined : offset,
            },
          })
        }
      />
    </div>
  );
}
